import { ReactNode } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import AppBar from "@mui/material/AppBar";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import IconButton from "@mui/material/IconButton";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MuiMenu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MenuLogo from "../../assets/menu-logo.png";
import { useAppSelector } from "../../app/hooks";
import { getFilteredMenu } from "./menuConfig";
import React from "react";
import ListItemButton from "@mui/material/ListItemButton";
import { Link, useLocation, useNavigate } from "react-router-dom";
import RoleSwitcher from "../widgets/RoleSwitcher";
import LogoutIcon from "@mui/icons-material/Logout";
import DailyStatusBanner from "../widgets/DailyStatusBanner";

type Props = {
  children: ReactNode;
};

const drawerWidth = 240;

const MenuItems = () => {
  const location = useLocation();
  const activeRole = useAppSelector((state) => state.auth.role);
  const isDeveloper = useAppSelector((state) => state.auth.isDeveloper);
  const menuConfig = getFilteredMenu(activeRole, isDeveloper);

  const sectionCount = Object.keys(menuConfig).length;

  return (
    <Box sx={{ overflow: "auto" }}>
      <List>
        {Object.entries(menuConfig).map(([section, items], index) => (
          <React.Fragment key={section}>
            {sectionCount > 1 && (
              <ListSubheader sx={{ lineHeight: "26px" }}>
                {section}
              </ListSubheader>
            )}
            {items.map((item) => (
              <ListItem
                key={item.label}
                disablePadding
                sx={{
                  backgroundColor: location.pathname.startsWith(
                    item.path.split(":")[0],
                  )
                    ? "#00000011"
                    : "transparent",
                }}
              >
                <ListItemButton component={Link} to={item.path}>
                  <ListItemIcon sx={{ minWidth: "36px" }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText sx={{ m: 0, p: 0 }} primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
            {sectionCount > 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

const Menu = ({ children }: Props) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const originalRole = useAppSelector((state) => state.auth.mainRole);
  const displayName = useAppSelector((state) => state.auth.displayName);
  const navigate = useNavigate();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAndGo = (url: string) => () => {
    setAnchorEl(null);
    navigate(url);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <img
            src={MenuLogo}
            alt="Black Mountain Labs"
            width={30}
            height={30}
          />
          &nbsp;
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Black Mountain Labs
          </Typography>
          <div>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Typography noWrap component="div">
                {displayName}&nbsp;
              </Typography>
              <AccountCircleIcon />
            </IconButton>
            <MuiMenu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem onClick={handleClose}>
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                {displayName}
              </MenuItem>
              <Divider />
              {originalRole === "Admin" ? (
                <MenuItem onClick={handleClose}>
                  <RoleSwitcher />
                </MenuItem>
              ) : null}
              <MenuItem onClick={handleCloseAndGo("/logout")}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                Logout
              </MenuItem>
            </MuiMenu>
          </div>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        <Toolbar />
        <MenuItems />
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, minHeight: "100vh" }}>
        <Toolbar />
        <DailyStatusBanner />
        {children}
      </Box>
    </Box>
  );
};

export default Menu;
